import { X, Printer } from "lucide-react";
import { createPortal } from "react-dom";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

export default function InventoryPrintReport({ materials, stockMap, year, month, lotsSummary, prevMonth, onClose }) {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-neutral-900">Previzualizare inventar tipărire</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Inventar luna {MONTHS_RO[month - 1]} {year} (A4)</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="inv-print-confirm"
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
                Tipărește
              </button>
              <button
                data-testid="inv-print-close"
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-900 p-1 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto thin-scroll p-6 bg-neutral-100">
            <div className="bg-white shadow-sm mx-auto" style={{ maxWidth: "210mm", padding: "12mm" }}>
              <InventoryBody materials={materials} stockMap={stockMap} year={year} month={month} lotsSummary={lotsSummary} prevMonth={prevMonth} />
            </div>
          </div>
        </div>
      </div>

      {createPortal(
        <div className="hidden print:block print-area">
          <InventoryBody materials={materials} stockMap={stockMap} year={year} month={month} lotsSummary={lotsSummary} prevMonth={prevMonth} />
        </div>,
        document.body
      )}
    </>
  );
}

function InventoryBody({ materials, stockMap, year, month, lotsSummary, prevMonth }) {
  const prevConsumption = prevMonth?.consumption || {};
  const prevInitial = prevMonth?.prevInitial || {};
  const prevLabel = prevMonth?.label || "";

  const rows = materials.map((name) => {
    const initial = parseFloat(stockMap[name]) || 0;
    const pConsum = prevConsumption[name] || 0;
    const pInit = prevInitial[name] || 0;
    return { name, initial, prev_consumption: pConsum, prev_initial: pInit };
  });
  const total = rows.reduce((s, r) => s + r.initial, 0);
  const totalPrevConsum = rows.reduce((s, r) => s + r.prev_consumption, 0);
  const filled = rows.filter((r) => r.initial > 0).length;
  const hasPrevData = totalPrevConsum > 0 || Object.keys(prevInitial).length > 0;

  const activeLots = lotsSummary
    ? Object.entries(lotsSummary)
        .filter(([, v]) => v.active_lot && v.remaining > 0.0001)
        .sort((a, b) => a[0].localeCompare(b[0]))
    : [];

  return (
    <div className="text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex items-end justify-between mb-6 pb-3 border-b-2 border-neutral-900 print-avoid-break">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Inventar Lunar Materii Prime</h1>
          <p className="text-xs text-neutral-600 mt-0.5">Brutăria LaDenis · stoc inițial începutul lunii</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-neutral-500 uppercase tracking-wider">Perioadă</p>
          <p className="text-sm font-semibold">{MONTHS_RO[month - 1]} {year}</p>
        </div>
      </div>

      <div className="mb-4 print-avoid-break grid grid-cols-4 gap-2 text-xs">
        <div className="border border-neutral-300 rounded p-2">
          <p className="text-neutral-500 uppercase tracking-wider text-[9px]">Total materii</p>
          <p className="font-bold tabular-nums">{rows.length}</p>
        </div>
        <div className="border border-neutral-300 rounded p-2">
          <p className="text-neutral-500 uppercase tracking-wider text-[9px]">Cu stoc</p>
          <p className="font-bold tabular-nums">{filled}</p>
        </div>
        <div className="border border-neutral-300 rounded p-2">
          <p className="text-neutral-500 uppercase tracking-wider text-[9px]">Total stoc (kg)</p>
          <p className="font-bold tabular-nums">{fmt(total)}</p>
        </div>
        <div className="border border-neutral-300 rounded p-2">
          <p className="text-neutral-500 uppercase tracking-wider text-[9px]">Consum {prevLabel} (kg)</p>
          <p className="font-bold tabular-nums">{fmt(totalPrevConsum)}</p>
        </div>
      </div>

      <div>
        <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
          Stoc inițial materii prime ({rows.length})
          {hasPrevData && <span className="text-neutral-500 normal-case font-normal"> · comparativ cu {prevLabel}</span>}
        </h2>
        <table className="print-table w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="border border-neutral-700 px-2 py-1 text-left w-7">#</th>
              <th className="border border-neutral-700 px-2 py-1 text-left">Materie primă</th>
              {hasPrevData && (
                <>
                  <th className="border border-neutral-700 px-2 py-1 text-right w-24">Stoc inițial {prevLabel} (kg)</th>
                  <th className="border border-neutral-700 px-2 py-1 text-right w-24">Consum {prevLabel} (kg)</th>
                </>
              )}
              <th className="border border-neutral-700 px-2 py-1 text-right w-24">Stoc inițial luna curentă (kg)</th>
              {hasPrevData && (
                <th className="border border-neutral-700 px-2 py-1 text-right w-24">Diferență (kg)</th>
              )}
              <th className="border border-neutral-700 px-2 py-1 text-right w-24">Verificat (kg)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              // Diferență = stoc_inițial_prev - consum_prev - stoc_inițial_curent
              // (cât a rămas teoretic - cât ai pe stoc acum = diferență pozitivă = lipsă inventar; negativă = în plus)
              const teoretic = r.prev_initial - r.prev_consumption;
              const diff = teoretic - r.initial;
              const hasDiff = hasPrevData && (r.prev_initial > 0 || r.prev_consumption > 0 || r.initial > 0);
              return (
                <tr key={r.name}>
                  <td className="border border-neutral-400 px-2 py-1 text-neutral-500">{idx + 1}</td>
                  <td className="border border-neutral-400 px-2 py-1 capitalize">{r.name}</td>
                  {hasPrevData && (
                    <>
                      <td className={`border border-neutral-400 px-2 py-1 text-right tabular-nums ${r.prev_initial > 0 ? "" : "text-neutral-400"}`}>
                        {r.prev_initial > 0 ? fmt(r.prev_initial) : "—"}
                      </td>
                      <td className={`border border-neutral-400 px-2 py-1 text-right tabular-nums ${r.prev_consumption > 0 ? "font-semibold text-amber-700" : "text-neutral-400"}`}>
                        {r.prev_consumption > 0 ? fmt(r.prev_consumption) : "—"}
                      </td>
                    </>
                  )}
                  <td className={`border border-neutral-400 px-2 py-1 text-right tabular-nums ${r.initial > 0 ? "font-semibold" : "text-neutral-400"}`}>
                    {r.initial > 0 ? fmt(r.initial) : "—"}
                  </td>
                  {hasPrevData && (
                    <td className={`border border-neutral-400 px-2 py-1 text-right tabular-nums ${
                      !hasDiff ? "text-neutral-400" :
                      Math.abs(diff) < 0.001 ? "text-neutral-500" :
                      diff > 0 ? "text-red-700 font-semibold" : "text-emerald-700 font-semibold"
                    }`}>
                      {!hasDiff ? "—" : (diff > 0 ? `+${fmt(diff)}` : fmt(diff))}
                    </td>
                  )}
                  <td className="border border-neutral-400 px-2 py-1"></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={hasPrevData ? 4 : 2} className="border border-neutral-700 px-2 py-1 font-bold text-right uppercase tracking-wider">Total (kg)</td>
              <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(total)}</td>
              {hasPrevData && <td className="border border-neutral-700 px-2 py-1"></td>}
              <td className="border border-neutral-700 px-2 py-1"></td>
            </tr>
          </tfoot>
        </table>
        {hasPrevData && (
          <p className="text-[10px] text-neutral-500 mt-1 italic">
            * Diferență = (Stoc inițial {prevLabel} − Consum {prevLabel}) − Stoc inițial luna curentă.
            <span className="text-red-700"> Pozitivă (+) = lipsă pe stoc</span>, <span className="text-emerald-700">negativă = surplus</span>.
          </p>
        )}
      </div>

      {activeLots.length > 0 && (
        <div className="mt-5 print-avoid-break">
          <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
            Loturi active materii prime ({activeLots.length})
          </h2>
          <table className="print-table w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="border border-neutral-700 px-2 py-1 text-left w-8">#</th>
                <th className="border border-neutral-700 px-2 py-1 text-left">Materie primă</th>
                <th className="border border-neutral-700 px-2 py-1 text-left w-24">Lot activ</th>
                <th className="border border-neutral-700 px-2 py-1 text-right w-28">kg rămase (lot)</th>
                <th className="border border-neutral-700 px-2 py-1 text-right w-28">Total rămas (kg)</th>
                <th className="border border-neutral-700 px-2 py-1 text-right w-16">Loturi</th>
              </tr>
            </thead>
            <tbody>
              {activeLots.map(([name, info], idx) => (
                <tr key={name}>
                  <td className="border border-neutral-400 px-2 py-1 text-neutral-500">{idx + 1}</td>
                  <td className="border border-neutral-400 px-2 py-1 capitalize">{name}</td>
                  <td className="border border-neutral-400 px-2 py-1 font-semibold">Lot {info.active_lot}</td>
                  <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums font-bold">{fmt(info.active_remaining ?? info.remaining)}</td>
                  <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums">{fmt(info.remaining)}</td>
                  <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums text-neutral-500">{info.lots_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-neutral-500 mt-1 italic">
            * Lotul activ = lotul cel mai vechi cu cantitate rămasă (FIFO). Se folosește primul.
          </p>
        </div>
      )}

      <div className="mt-8 pt-4 border-t border-neutral-300 grid grid-cols-3 gap-6 text-xs text-neutral-600 print-avoid-break">
        <div>
          <p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Întocmit</p>
          <div className="border-b border-neutral-400 h-6"></div>
        </div>
        <div>
          <p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Verificat</p>
          <div className="border-b border-neutral-400 h-6"></div>
        </div>
        <div>
          <p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Aprobat</p>
          <div className="border-b border-neutral-400 h-6"></div>
        </div>
      </div>
    </div>
  );
}
